import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import {
  View,
  Text,
  Animated,
  PanResponder,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
  FlatList,
  RefreshControl,
} from 'react-native';
import { createStyles, getScreenWidth, getColors } from './DbTabView.styles';
import type {
  DbTabViewProps,
  DbTabViewRef,
  CachedPanelData,
  TabViewItem,
  PanelRenderProps,
} from './DbTabView.types';
import DbScrollTab from '../DbScrollTab';

const SCREEN_WIDTH = getScreenWidth();

// 创建初始缓存数据
const createInitialCache = <T,>(): CachedPanelData<T> => ({
  data: [],
  hasMore: true,
  loadingState: 'idle',
  scrollOffset: 0,
  page: 1,
  initialized: false,
  lastUpdated: undefined,
});

const DbTabView = forwardRef<DbTabViewRef, DbTabViewProps>((props, ref) => {
  const {
    tabs,
    activeKey: controlledActiveKey,
    defaultActiveKey,
    onChange,
    // Tab 栏配置
    tabPosition = 'top',
    tabScrollable = true,
    showIndicator = true,
    indicatorColor = '#FF2442',
    indicatorWidth,
    scrollToLeft = true,
    renderTab,
    // 滑动配置
    swipeable = true,
    swipeThreshold = 0.3,
    swipeVelocityThreshold = 0.5,
    lazy = true,
    preloadOffset = 1,
    // 缓存配置
    cacheEnabled = true,
    cacheExpiry = 0,
    maxCachedPanels = 10,
    initialCache,
    // 数据加载
    loadData,
    pageSize = 20,
    autoLoad = true,
    // 面板渲染
    renderPanel,
    renderItem,
    keyExtractor,
    useBuiltInList = false,
    // 列表配置
    refreshable = true,
    showSeparator = false,
    renderEmpty,
    renderLoading,
    renderError,
    // 样式
    style,
    tabBarStyle,
    tabStyle,
    tabTextStyle,
    activeTabTextStyle,
    panelContainerStyle,
    panelStyle,
  } = props;

  const styles = useMemo(() => createStyles('light'), []);
  const colors = useMemo(() => getColors('light'), []);

  // 状态
  const [activeKey, setActiveKey] = useState(
    controlledActiveKey || defaultActiveKey || tabs[0]?.key || ''
  );
  const [cache, setCache] = useState<Record<string, CachedPanelData<any>>>(() => {
    const initial: Record<string, CachedPanelData<any>> = {};
    tabs.forEach((tab) => {
      initial[tab.key] = initialCache?.[tab.key] || createInitialCache();
    });
    return initial;
  });
  const [renderedTabs, setRenderedTabs] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    initial.add(activeKey);
    return initial;
  });

  // Refs
  const translateX = useRef(new Animated.Value(0)).current;
  const currentIndex = useRef(tabs.findIndex((t) => t.key === activeKey) || 0);
  const isAnimating = useRef(false);
  const flatListRefs = useRef<Record<string, FlatList<any> | null>>({});

  // 受控模式
  const currentActiveKey = controlledActiveKey !== undefined ? controlledActiveKey : activeKey;
  const currentActiveIndex = tabs.findIndex((t) => t.key === currentActiveKey);

  // 更新动画位置
  useEffect(() => {
    const newIndex = tabs.findIndex((t) => t.key === currentActiveKey);
    if (newIndex !== -1 && newIndex !== currentIndex.current) {
      currentIndex.current = newIndex;
      Animated.spring(translateX, {
        toValue: -newIndex * SCREEN_WIDTH,
        useNativeDriver: true,
        tension: 100,
        friction: 20,
      }).start();
    }
  }, [currentActiveKey, tabs, translateX]);

  // 惰性加载：更新已渲染的 Tab
  useEffect(() => {
    if (lazy) {
      setRenderedTabs((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentActiveKey);
        
        // 预加载相邻面板
        for (let i = 1; i <= preloadOffset; i++) {
          const prevIndex = currentActiveIndex - i;
          const nextIndex = currentActiveIndex + i;
          if (prevIndex >= 0) {
            newSet.add(tabs[prevIndex].key);
          }
          if (nextIndex < tabs.length) {
            newSet.add(tabs[nextIndex].key);
          }
        }
        
        return newSet;
      });
    }
  }, [currentActiveKey, currentActiveIndex, lazy, preloadOffset, tabs]);

  // 自动加载首屏数据
  useEffect(() => {
    if (autoLoad && loadData && !cache[currentActiveKey]?.initialized) {
      loadTabData(currentActiveKey, true);
    }
  }, [currentActiveKey, autoLoad, loadData]);

  // ==================== 缓存管理 ====================

  const updateCache = useCallback(
    (key: string, data: Partial<CachedPanelData<any>>) => {
      if (!cacheEnabled) return;

      setCache((prev) => {
        const newCache = { ...prev };
        newCache[key] = {
          ...prev[key],
          ...data,
          lastUpdated: Date.now(),
        };

        // 限制缓存数量
        const keys = Object.keys(newCache);
        if (keys.length > maxCachedPanels) {
          // 找出最久未更新的缓存（排除当前激活的）
          const sortedKeys = keys
            .filter((k) => k !== currentActiveKey)
            .sort((a, b) => (newCache[a].lastUpdated || 0) - (newCache[b].lastUpdated || 0));
          
          const keysToRemove = sortedKeys.slice(0, keys.length - maxCachedPanels);
          keysToRemove.forEach((k) => {
            newCache[k] = createInitialCache();
          });
        }

        return newCache;
      });
    },
    [cacheEnabled, maxCachedPanels, currentActiveKey]
  );

  const isCacheValid = useCallback(
    (key: string): boolean => {
      if (!cacheEnabled) return false;
      const cached = cache[key];
      if (!cached?.initialized) return false;
      if (cacheExpiry === 0) return true;
      if (!cached.lastUpdated) return false;
      return Date.now() - cached.lastUpdated < cacheExpiry;
    },
    [cache, cacheEnabled, cacheExpiry]
  );

  // ==================== 数据加载 ====================

  const loadTabData = useCallback(
    async (key: string, isRefresh: boolean = false) => {
      if (!loadData) return;

      const currentCache = cache[key] || createInitialCache();
      
      // 如果正在加载，不重复请求
      if (currentCache.loadingState === 'loading' || currentCache.loadingState === 'loadingMore') {
        return;
      }

      // 如果不是刷新且缓存有效，不请求
      if (!isRefresh && isCacheValid(key)) {
        return;
      }

      const page = isRefresh ? 1 : (currentCache.page || 1);
      
      updateCache(key, {
        loadingState: isRefresh ? 'refreshing' : (page === 1 ? 'loading' : 'loadingMore'),
      });

      try {
        const result = await loadData({
          key,
          page,
          pageSize,
          isRefresh,
        });

        updateCache(key, {
          data: isRefresh ? result.data : [...currentCache.data, ...result.data],
          hasMore: result.hasMore,
          loadingState: 'idle',
          page: page + 1,
          initialized: true,
        });
      } catch (error) {
        updateCache(key, {
          loadingState: 'idle',
        });
        console.error(`Failed to load data for tab ${key}:`, error);
      }
    },
    [loadData, cache, isCacheValid, updateCache, pageSize]
  );

  const refreshTabData = useCallback(
    (key: string) => {
      loadTabData(key, true);
    },
    [loadTabData]
  );

  const loadMoreTabData = useCallback(
    (key: string) => {
      const currentCache = cache[key];
      if (currentCache?.hasMore && currentCache.loadingState === 'idle') {
        loadTabData(key, false);
      }
    },
    [cache, loadTabData]
  );

  // ==================== 切换逻辑 ====================

  const switchToIndex = useCallback(
    (index: number, animated: boolean = true) => {
      if (index < 0 || index >= tabs.length) return;
      if (tabs[index].disabled) return;
      if (isAnimating.current) return;

      const newKey = tabs[index].key;
      const isSameTab = newKey === currentActiveKey;
      
      // 即使是同一个 Tab，也需要执行回弹动画
      isAnimating.current = true;
      currentIndex.current = index;

      // 只有切换到不同 Tab 时才触发状态更新和回调
      if (!isSameTab) {
        if (controlledActiveKey === undefined) {
          setActiveKey(newKey);
        }
        onChange?.(newKey, index);
      }

      if (animated) {
        Animated.spring(translateX, {
          toValue: -index * SCREEN_WIDTH,
          useNativeDriver: true,
          tension: 100,
          friction: 20,
        }).start(() => {
          isAnimating.current = false;
        });
      } else {
        translateX.setValue(-index * SCREEN_WIDTH);
        isAnimating.current = false;
      }

      // 自动加载新 Tab 的数据（仅切换时）
      if (!isSameTab && autoLoad && loadData && !cache[newKey]?.initialized) {
        loadTabData(newKey, true);
      }
    },
    [tabs, currentActiveKey, controlledActiveKey, onChange, translateX, autoLoad, loadData, cache, loadTabData]
  );

  const switchTo = useCallback(
    (key: string) => {
      const index = tabs.findIndex((t) => t.key === key);
      if (index !== -1) {
        switchToIndex(index);
      }
    },
    [tabs, switchToIndex]
  );

  // ==================== 滑动手势 ====================

  const panResponder = useMemo(() => {
    if (!swipeable) return null;

    let startX = 0;
    let startTime = 0;

    return PanResponder.create({
      onMoveShouldSetPanResponder: (_, gestureState) => {
        // 判断是否为水平滑动
        const { dx, dy } = gestureState;
        return Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10;
      },
      onMoveShouldSetPanResponderCapture: (_, gestureState) => {
        const { dx, dy } = gestureState;
        return Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 15;
      },
      onPanResponderGrant: () => {
        startX = currentIndex.current * SCREEN_WIDTH;
        startTime = Date.now();
        translateX.stopAnimation();
        // 不使用 offset，直接基于当前位置计算
      },
      onPanResponderMove: (_, gestureState) => {
        const { dx } = gestureState;
        
        // 计算新位置
        const basePosition = -currentIndex.current * SCREEN_WIDTH;
        let newPosition = basePosition + dx;
        
        // 边界检查和阻尼效果
        const maxPosition = 0; // 第一个面板的位置
        const minPosition = -(tabs.length - 1) * SCREEN_WIDTH; // 最后一个面板的位置
        
        if (newPosition > maxPosition) {
          // 左边界阻尼
          const overflow = newPosition - maxPosition;
          newPosition = maxPosition + overflow * 0.3;
        } else if (newPosition < minPosition) {
          // 右边界阻尼
          const overflow = newPosition - minPosition;
          newPosition = minPosition + overflow * 0.3;
        }
        
        translateX.setValue(newPosition);
      },
      onPanResponderRelease: (_, gestureState) => {
        const { dx, vx } = gestureState;
        const duration = Date.now() - startTime;
        const velocity = Math.abs(vx);
        
        let targetIndex = currentIndex.current;
        
        // 判断是否切换
        if (velocity > swipeVelocityThreshold && duration < 300) {
          // 快速滑动
          if (dx > 0 && currentIndex.current > 0) {
            targetIndex = currentIndex.current - 1;
          } else if (dx < 0 && currentIndex.current < tabs.length - 1) {
            targetIndex = currentIndex.current + 1;
          }
        } else if (Math.abs(dx) > SCREEN_WIDTH * swipeThreshold) {
          // 滑动距离超过阈值
          if (dx > 0 && currentIndex.current > 0) {
            targetIndex = currentIndex.current - 1;
          } else if (dx < 0 && currentIndex.current < tabs.length - 1) {
            targetIndex = currentIndex.current + 1;
          }
        }
        
        // 确保 targetIndex 在有效范围内
        targetIndex = Math.max(0, Math.min(tabs.length - 1, targetIndex));
        
        // 跳过禁用的 Tab
        while (tabs[targetIndex]?.disabled) {
          if (targetIndex > currentIndex.current) {
            targetIndex++;
          } else if (targetIndex < currentIndex.current) {
            targetIndex--;
          } else {
            break;
          }
          if (targetIndex < 0 || targetIndex >= tabs.length) {
            targetIndex = currentIndex.current;
            break;
          }
        }
        
        // 执行切换
        switchToIndex(targetIndex);
      },
      onPanResponderTerminate: () => {
        // 回到当前位置
        Animated.spring(translateX, {
          toValue: -currentIndex.current * SCREEN_WIDTH,
          useNativeDriver: true,
          tension: 100,
          friction: 20,
        }).start();
      },
    });
  }, [swipeable, tabs, translateX, swipeThreshold, swipeVelocityThreshold, switchToIndex]);

  // ==================== 暴露方法 ====================

  useImperativeHandle(ref, () => ({
    switchTo,
    switchToIndex: (index: number) => switchToIndex(index),
    refreshCurrent: () => refreshTabData(currentActiveKey),
    refresh: refreshTabData,
    refreshAll: () => tabs.forEach((tab) => refreshTabData(tab.key)),
    getCache: (key: string) => cache[key],
    clearCache: (key?: string) => {
      if (key) {
        setCache((prev) => ({
          ...prev,
          [key]: createInitialCache(),
        }));
      } else {
        setCache(() => {
          const newCache: Record<string, CachedPanelData<any>> = {};
          tabs.forEach((tab) => {
            newCache[tab.key] = createInitialCache();
          });
          return newCache;
        });
      }
    },
    getActiveKey: () => currentActiveKey,
  }));

  // ==================== 渲染 Tab 栏 ====================

  const renderTabBar = () => {
    const tabItems = tabs.map((tab) => ({
      key: tab.key,
      title: tab.title || tab.key,
      badge: tab.badge,
      icon: tab.icon,
      extra: tab.extra,
      disabled: tab.disabled,
    }));

    return (
      <View style={[styles.tabBarContainer, tabPosition === 'bottom' && styles.tabBarContainerBottom, tabBarStyle]}>
        <DbScrollTab
          tabs={tabItems}
          activeKey={currentActiveKey}
          onChange={(key) => switchTo(key)}
          scrollable={tabScrollable}
          showIndicator={showIndicator}
          indicatorColor={indicatorColor}
          indicatorWidth={indicatorWidth}
          scrollToLeft={scrollToLeft}
          renderTab={renderTab as any}
          tabStyle={tabStyle}
          tabTextStyle={tabTextStyle}
          activeTabTextStyle={activeTabTextStyle}
        />
      </View>
    );
  };

  // ==================== 渲染内置列表 ====================

  const renderBuiltInList = (tab: TabViewItem, panelCache: CachedPanelData<any>) => {
    const isLoading = panelCache.loadingState === 'loading';
    const isRefreshing = panelCache.loadingState === 'refreshing';

    if (isLoading && panelCache.data.length === 0) {
      if (renderLoading) {
        return renderLoading();
      }
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.indicator} />
        </View>
      );
    }

    if (panelCache.data.length === 0 && panelCache.initialized) {
      if (renderEmpty) {
        return renderEmpty(tab.key);
      }
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>暂无数据</Text>
        </View>
      );
    }

    return (
      <FlatList
        ref={(r) => {
          flatListRefs.current[tab.key] = r;
        }}
        data={panelCache.data}
        keyExtractor={keyExtractor || ((item, index) => `${tab.key}-${index}`)}
        renderItem={({ item, index }) => {
          const result = renderItem?.(item, index, tab.key);
          return result ? <>{result}</> : null;
        }}
        refreshControl={
          refreshable ? (
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={() => refreshTabData(tab.key)}
              colors={[indicatorColor]}
              tintColor={indicatorColor}
            />
          ) : undefined
        }
        onEndReached={() => loadMoreTabData(tab.key)}
        onEndReachedThreshold={0.5}
        ItemSeparatorComponent={
          showSeparator ? () => <View style={styles.listSeparator} /> : undefined
        }
        ListFooterComponent={() => {
          if (panelCache.loadingState === 'loadingMore') {
            return (
              <View style={{ padding: 16, alignItems: 'center' }}>
                <ActivityIndicator size="small" color={colors.indicator} />
              </View>
            );
          }
          if (!panelCache.hasMore && panelCache.data.length > 0) {
            return (
              <View style={{ padding: 16, alignItems: 'center' }}>
                <Text style={{ fontSize: 12, color: '#999' }}>没有更多了</Text>
              </View>
            );
          }
          return null;
        }}
        showsVerticalScrollIndicator={false}
      />
    );
  };

  // ==================== 渲染面板 ====================

  const renderPanelContent = (tab: TabViewItem, index: number) => {
    const isActive = tab.key === currentActiveKey;
    const panelCache = cache[tab.key] || createInitialCache();

    // 惰性加载检查
    if (lazy && !renderedTabs.has(tab.key)) {
      return <View style={styles.panel} />;
    }

    // 自定义面板渲染
    if (renderPanel) {
      const panelProps: PanelRenderProps = {
        item: tab,
        index,
        isActive,
        cachedData: panelCache,
        updateCache: (data) => updateCache(tab.key, data),
        refresh: () => refreshTabData(tab.key),
        loadMore: () => loadMoreTabData(tab.key),
      };
      return (
        <View style={[styles.panel, panelStyle]}>
          {renderPanel(panelProps)}
        </View>
      );
    }

    // 使用内置列表
    if (useBuiltInList && renderItem) {
      return (
        <View style={[styles.panel, panelStyle]}>
          {renderBuiltInList(tab, panelCache)}
        </View>
      );
    }

    // 默认空面板
    return (
      <View style={[styles.panel, panelStyle]}>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>请配置 renderPanel 或 renderItem</Text>
        </View>
      </View>
    );
  };

  // ==================== 渲染面板容器 ====================

  const renderPanels = () => {
    return (
      <View style={[styles.panelContainer, panelContainerStyle]}>
        <Animated.View
          style={[
            styles.swipeContainer,
            {
              width: SCREEN_WIDTH * tabs.length,
              transform: [{ translateX }],
            },
          ]}
          {...(panResponder?.panHandlers || {})}
        >
          {tabs.map((tab, index) => (
            <View key={tab.key} style={styles.panelWrapper}>
              {renderPanelContent(tab, index)}
            </View>
          ))}
        </Animated.View>
      </View>
    );
  };

  // ==================== 主渲染 ====================

  return (
    <View style={[styles.container, style]}>
      {tabPosition === 'top' && renderTabBar()}
      {renderPanels()}
      {tabPosition === 'bottom' && renderTabBar()}
    </View>
  );
});

DbTabView.displayName = 'DbTabView';

export default DbTabView;
export type {
  DbTabViewProps,
  DbTabViewRef,
  TabViewItem,
  CachedPanelData,
  PanelRenderProps,
  LoadDataParams,
  LoadDataResult,
} from './DbTabView.types';
